import { X, Plus, Trash2 } from "lucide-react";
import ImageUploader from "../../components/ImageUploader";

const BADGES = ["", "NEW", "HOT", "SALE", "RESTOCKED"];
const inp = "w-full bg-white border border-[#E8E4D9] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--brand-primary)]";

const newVariant = () => ({
  id: Math.random().toString(36).slice(2, 10),
  sku: "", size: "", color: "", pack: "", price: "", sale_price: "", stock: 0, reserved: 0, sold: 0, image_url: "",
});

export default function AdminProductForm({ form, setForm, editing, allCats, onSave, onClose }) {
  const selectedCat = allCats.find((c) => c.key === form.category);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const images = form.images && form.images.length ? form.images : (form.image_url ? [form.image_url] : []);
  const setImages = (imgs) => setForm({ ...form, images: imgs, image_url: imgs[0] || "" });
  const addImage = () => setImages([...images, ""]);
  const setImage = (i, v) => setImages(images.map((x, idx) => (idx === i ? v : x)));
  const removeImage = (i) => setImages(images.filter((_, idx) => idx !== i));

  const variants = form.variants || [];
  const setVariants = (vs) => setForm({ ...form, variants: vs });
  const addVariant = () => setVariants([...variants, newVariant()]);
  const setVariant = (i, k, v) => setVariants(variants.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeVariant = (i) => setVariants(variants.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSave} className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg text-[#1A231C]">{editing ? "Edit Product" : "Add Product"}</h3>
          <button type="button" onClick={onClose} data-testid="product-form-close"><X className="w-5 h-5 text-[#5C665D]" /></button>
        </div>

        <div className="space-y-3">
          <input data-testid="product-form-name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Product name" className={inp} />
          <textarea data-testid="product-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={2} className={inp} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#5C665D]">Base Price</label>
              <input data-testid="product-form-price" required type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Price" className={inp} />
            </div>
            <div>
              <label className="text-xs text-[#5C665D]">Sale Price (optional)</label>
              <input data-testid="product-form-sale-price" type="number" step="0.01" value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value)} placeholder="Sale price" className={inp} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#5C665D]">Category</label>
              <select data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })} className={inp}>
                {allCats.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
              </select>
            </div>
            {selectedCat && (selectedCat.subcategories || []).length > 0 ? (
              <div>
                <label className="text-xs text-[#5C665D]">Sub-category</label>
                <select data-testid="product-form-subcategory" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} className={inp}>
                  <option value="">Select</option>
                  {selectedCat.subcategories.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
                </select>
              </div>
            ) : (
              <div>
                <label className="text-xs text-[#5C665D]">Available Stock {variants.length > 0 ? "(uses variants)" : ""}</label>
                <input data-testid="product-form-stock" type="number" value={form.stock ?? 0} onChange={(e) => set("stock", parseInt(e.target.value) || 0)} className={inp} disabled={variants.length > 0} />
              </div>
            )}
          </div>

          {selectedCat && (selectedCat.subcategories || []).length > 0 && (
            <div>
              <label className="text-xs text-[#5C665D]">Available Stock {variants.length > 0 ? "(uses variants)" : ""}</label>
              <input data-testid="product-form-stock" type="number" value={form.stock ?? 0} onChange={(e) => set("stock", parseInt(e.target.value) || 0)} className={inp} disabled={variants.length > 0} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#5C665D]">Unit</label>
              <input data-testid="product-form-unit" value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="e.g. per kg, each" className={inp} />
            </div>
            <div>
              <label className="text-xs text-[#5C665D]">Badge</label>
              <select data-testid="product-form-badge" value={form.badge} onChange={(e) => set("badge", e.target.value)} className={inp}>
                {BADGES.map((b) => (<option key={b} value={b}>{b || "None"}</option>))}
              </select>
            </div>
          </div>

          {/* Image gallery */}
          <div className="border border-[#E8E4D9] rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[#1A231C]">Image Gallery</label>
              <button type="button" data-testid="product-add-image" onClick={addImage} className="text-sm text-[var(--brand-primary)] inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add image</button>
            </div>
            {images.length === 0 && <ImageUploader label="" testid="product-image" value="" onChange={(v) => setImages([v])} aspect="aspect-square" />}
            <div className="space-y-3">
              {images.map((img, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <div className="flex-1">
                    <ImageUploader label={i === 0 ? "Primary image" : `Image ${i + 1}`} testid={`product-image-${i}`} value={img} onChange={(v) => setImage(i, v)} aspect="aspect-square" />
                  </div>
                  <button type="button" data-testid={`product-remove-image-${i}`} onClick={() => removeImage(i)} className="text-[var(--brand-accent)] p-1.5 mt-6"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Variants */}
          <div className="border border-[#E8E4D9] rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-sm font-medium text-[#1A231C]">Variants (size / color)</label>
                <p className="text-xs text-[#5C665D]">Add if this product comes in options. Leave empty for a simple product.</p>
              </div>
              <button type="button" data-testid="product-add-variant" onClick={addVariant} className="text-sm text-[var(--brand-primary)] inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add variant</button>
            </div>
            <div className="space-y-3">
              {variants.map((v, i) => (
                <div key={v.id} data-testid={`variant-row-${i}`} className="border border-[#E8E4D9] rounded-lg p-3 bg-[#FCFBF8]">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <input data-testid={`variant-size-${i}`} value={v.size} onChange={(e) => setVariant(i, "size", e.target.value)} placeholder="Size" className={inp} />
                    <input data-testid={`variant-color-${i}`} value={v.color} onChange={(e) => setVariant(i, "color", e.target.value)} placeholder="Color" className={inp} />
                    <input data-testid={`variant-pack-${i}`} value={v.pack || ""} onChange={(e) => setVariant(i, "pack", e.target.value)} placeholder="Pack (e.g. 6-Pack)" className={inp} />
                    <input data-testid={`variant-sku-${i}`} value={v.sku} onChange={(e) => setVariant(i, "sku", e.target.value)} placeholder="SKU" className={inp} />
                    <input data-testid={`variant-price-${i}`} type="number" step="0.01" value={v.price ?? ""} onChange={(e) => setVariant(i, "price", e.target.value)} placeholder="Price" className={inp} />
                    <input data-testid={`variant-sale-${i}`} type="number" step="0.01" value={v.sale_price ?? ""} onChange={(e) => setVariant(i, "sale_price", e.target.value)} placeholder="Sale price" className={inp} />
                    <input data-testid={`variant-stock-${i}`} type="number" value={v.stock ?? 0} onChange={(e) => setVariant(i, "stock", parseInt(e.target.value) || 0)} placeholder="Stock" className={inp} />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-[#5C665D]">Reserved: {v.reserved || 0} · Sold: {v.sold || 0}</span>
                    <button type="button" data-testid={`variant-remove-${i}`} onClick={() => removeVariant(i)} className="text-[var(--brand-accent)] text-sm inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[#1A231C]">
            <input data-testid="product-form-instock" type="checkbox" checked={form.in_stock} onChange={(e) => set("in_stock", e.target.checked)} /> Product is active / purchasable
          </label>
        </div>

        <button data-testid="product-form-save" type="submit" className="w-full mt-5 bg-[var(--brand-primary)] text-white px-6 py-3 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors">
          {editing ? "Update" : "Add"} Product
        </button>
      </form>
    </div>
  );
}
